import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Key, AlertCircle, CheckCircle2, Loader2, Shield, ArrowRight, Library, Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import api, { imgUrl } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { useLocalizedNav } from "@/i18n/LocalizedLink";
import LoginRequiredModal from "@/components/LoginRequiredModal";

export default function Verify() {
  const { t } = useLang();
  const { user, loading } = useUserAuth();
  const lnav = useLocalizedNav();
  const qc = useQueryClient();
  const [step, setStep] = useState("input"); // input | choose | done
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [session, setSession] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const inFlight = useRef(false);
  const claimed = useRef(new Set());

  const verify = async (e) => {
    e?.preventDefault();
    if (!user) return;
    if (!code.trim()) return;
    setVerifying(true);
    try {
      const r = await api.post("/public/verify-key", { code: code.trim() });
      setSession(r.data);
      setStep("choose");
      toast.success(t("verify.verified_toast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setVerifying(false);
    }
  };

  const selectProduct = async (product_id) => {
    if (inFlight.current) return;
    if (claimed.current.has(product_id)) return;
    if (!session?.session_token) return;
    inFlight.current = true;
    claimed.current.add(product_id);
    setDelivering(true);
    try {
      const r = await api.post("/public/select-product", {
        session_token: session.session_token,
        product_id,
      });
      if (r.data.uses_remaining > 0 && r.data.session_token) {
        setSession((s) => ({
          ...s,
          session_token: r.data.session_token,
          uses_remaining: r.data.uses_remaining,
          products: r.data.products || [],
        }));
        // Refresh the user's library so the game appears immediately.
        if (user) qc.invalidateQueries({ queryKey: ["library"] });
        toast.success(
          t("verify.game_added_prefix") +
            ` ${r.data.uses_remaining}/${r.data.max_uses} ` +
            t("verify.uses_remaining_suffix")
        );
      } else {
        setDelivery(r.data);
        setStep("done");
        // Refresh the user's library so the game appears immediately.
        if (user) qc.invalidateQueries({ queryKey: ["library"] });
        toast.success(t("verify.delivered_toast"));
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        // Product already owned — silently refresh and continue.
        try {
          const refresh = await api.post("/public/verify-key", { code: session.key_code });
          if (refresh.data.uses_remaining > 0 && refresh.data.session_token) {
            setSession((s) => ({
              ...s,
              session_token: refresh.data.session_token,
              uses_remaining: refresh.data.uses_remaining,
              products: refresh.data.products || [],
            }));
          }
        } catch (_e) { /* ignore */ }
      } else {
        claimed.current.delete(product_id);
        toast.error(err?.response?.data?.detail || t("common.error"));
      }
    } finally {
      inFlight.current = false;
      setDelivering(false);
    }
  };

  const copy = (v) => { try { navigator.clipboard.writeText(v || ""); toast.success("Copied"); } catch { /* ignore */ } };

  if (loading) {
    return <main className="min-h-[80vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-brand-cyan" /></main>;
  }

  if (!user) {
    return (
      <main className="relative min-h-[80vh]" data-testid="verify-login-required">
        <div className="aurora" />
        <LoginRequiredModal open onClose={() => lnav("/")} next="/verify" />
      </main>
    );
  }

  return (
    <main className="relative min-h-[80vh]" data-testid="verify-page">
      <div className="aurora" />
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24 relative">
        <AnimatePresence mode="wait">
          {step === "input" && (
            <motion.div key="input" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/5 mb-6">
                <Key className="w-3.5 h-3.5 text-brand-cyan" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan">{t("nav.verify")}</span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">{t("verify.title")}</h1>
              <p className="text-zinc-400 mb-10 max-w-lg mx-auto">{t("verify.subtitle")}</p>
              <form onSubmit={verify} className="max-w-xl mx-auto">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t("verify.placeholder")}
                  className="key-input w-full text-xl md:text-2xl px-6 py-6 rounded-xl text-white placeholder:text-zinc-700"
                  data-testid="verify-key-input"
                  autoFocus
                />
                <button type="submit" disabled={!code.trim() || verifying} className="mt-5 w-full bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-semibold px-6 py-4 rounded-xl glow-blue transition-all inline-flex items-center justify-center gap-2" data-testid="verify-submit-btn">
                  {verifying ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("verify.verifying")}</> : <><Shield className="w-4 h-4" /> {t("verify.submit")}</>}
                </button>
              </form>
            </motion.div>
          )}

          {step === "choose" && session && (
            <motion.div key="choose" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-brand-green" />
                <span className="text-sm text-brand-green font-semibold">Verified · {session.key_code}</span>
                {session.max_uses > 1 && (
                  <span className="ms-auto px-3 py-1 rounded-full bg-brand/10 border border-brand/30 text-xs font-mono font-bold text-brand-cyan" data-testid="uses-remaining-badge">
                    {session.uses_remaining}/{session.max_uses} {t("verify.uses_remaining_suffix")}
                  </span>
                )}
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-2">{t("verify.pick_game")}</h2>
              <p className="text-zinc-400 mb-8">{t("verify.pick_sub")}</p>

              {session.products.length === 0 ? (
                <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-xl p-6 text-yellow-300">
                  <AlertCircle className="w-5 h-5 inline me-2" />
                  {t("verify.no_products")}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {session.products.map((p) => (
                    <button
                      key={p.id}
                      disabled={delivering}
                      onClick={() => selectProduct(p.id)}
                      className="group relative bg-[#0c0c0c] border border-white/5 rounded-xl overflow-hidden text-start disabled:opacity-50 enabled:hover:border-brand transition-all"
                      data-testid={`select-product-${p.id}`}
                    >
                      <div className="aspect-[16/9] bg-[#080808] overflow-hidden relative">
                        {p.image_url && <img src={imgUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute top-2 start-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30">{p.platform}</span>
                        </div>
                      </div>
                      <div className="p-4 flex items-center justify-between">
                        <h3 className="font-heading font-semibold">{p.title}</h3>
                        <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-brand-cyan group-hover:translate-x-1 transition-all rtl-flip" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {delivering && <div className="mt-6 text-center text-zinc-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("verify.delivering")}</div>}
            </motion.div>
          )}

          {step === "done" && delivery && (
            <motion.div key="done" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-green/10 border border-brand-green/30 mb-4 pulse-glow">
                  <CheckCircle2 className="w-8 h-8 text-brand-green" />
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-2">{delivery.product.title}</h2>
                <p className="text-zinc-400">{t("verify.warning_save")}</p>
              </div>

              {/* Credentials are shown only after an authenticated redemption. */}
              <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6 space-y-4 max-w-xl mx-auto">
                <Cred label="Steam Username" value={delivery.steam_username} onCopy={() => copy(delivery.steam_username)} testid="cred-username" />
                <Cred label="Steam Password" value={showPw ? delivery.steam_password : "••••••••••"}
                      onCopy={() => copy(delivery.steam_password)}
                      right={<button onClick={() => setShowPw((v) => !v)} className="text-zinc-500 hover:text-white p-1" data-testid="toggle-password">
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>}
                      testid="cred-password" />
              </div>

              <div className="mt-8 text-center">
                <button onClick={() => lnav("/profile")} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-7 py-4 rounded-lg glow-blue" data-testid="goto-library">
                  <Library className="w-4 h-4" /> {t("verify.view_library")}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}

function Cred({ label, value, onCopy, right, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">{label}</div>
      <div className="flex items-center gap-2 bg-[#080808] border border-white/10 rounded-lg px-4 py-3">
        <span className="flex-1 font-mono text-sm break-all" data-testid={testid}>{value}</span>
        {right}
        <button onClick={onCopy} className="text-zinc-500 hover:text-white p-1" data-testid={`${testid}-copy`}>
          <Copy className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
